import { parseTradeCalendarDate } from "./tradeFinancialUtils";

export const COMMISSION_REPORT_CLASSIFICATIONS = [
  { value: "ALL", label: "All Classifications" },
  { value: "LISTING SIDE", label: "Listing Side" },
  { value: "CO-OPERATING SIDE", label: "Co-operating Side" },
  { value: "AGENT DOUBLE ENDER", label: "Agent Double Ender" },
];

export const COMMISSION_REPORT_SORT_OPTIONS = [
  { value: "offerDate", label: "Offer Date" },
  { value: "firmDate", label: "Firm Date" },
  { value: "finalizedDate", label: "Finalized Date" },
  { value: "entryDate", label: "Entry Date" },
];

const PLAN_DISPLAY_NAMES = {
  referralBroker: "Referral Broker",
  plan250: "Plan 250",
  plan500: "Plan 500",
  plan9010: "Plan 90/10",
  plan955: "Plan 95/5",
  plan8515: "Plan 85/15",
  plan5050: "Plan 50/50",
  plan8020: "Plan 80/20",
  plan150: "Plan 150",
  flatFee: "Flat Fee",
  garnishment: "Garnishment",
  buyerRebate: "Buyer Rebate",
  noFee: "No Fee",
};

export function getPlanDisplayName(feeInfo) {
  if (!feeInfo) return "";
  return PLAN_DISPLAY_NAMES[feeInfo] || feeInfo;
}

export function classificationCode(classification) {
  const c = String(classification || "")
    .trim()
    .toUpperCase();
  if (c === "LISTING SIDE") return "L";
  if (c === "CO-OPERATING SIDE") return "C";
  if (c === "AGENT DOUBLE ENDER") return "D";
  return c ? c.charAt(0) : "";
}

export function formatAgentSortName(agent) {
  if (!agent) return "";
  const last = String(agent.lastName || "").trim();
  const first = String(agent.firstName || "").trim();
  if (last && first) return `${last}, ${first}`;
  return [first, last].filter(Boolean).join(" ").trim();
}

export function formatAgentGroupLabel(agentOrRow) {
  const emp =
    agentOrRow?.employeeNo != null && String(agentOrRow.employeeNo).trim() !== ""
      ? String(agentOrRow.employeeNo).trim()
      : agentOrRow?.agentId != null && String(agentOrRow.agentId).trim() !== ""
        ? String(agentOrRow.agentId).trim()
        : "";
  const name =
    formatAgentSortName(agentOrRow) ||
    String(agentOrRow?.agentName || "").trim() ||
    "Unknown Agent";
  return emp ? `${emp} ${name}` : name;
}

function isReferralRow(row) {
  return String(row?.classification || "")
    .trim()
    .toUpperCase() === "REFERRAL";
}

export function getTradeSortDate(trade, sortBy) {
  const k = trade?.keyInfo || {};
  switch (sortBy) {
    case "firmDate":
      return parseTradeCalendarDate(k.firmDate);
    case "finalizedDate":
      return parseTradeCalendarDate(k.finalizedDate);
    case "entryDate":
      return (
        parseTradeCalendarDate(k.entryDate) ||
        parseTradeCalendarDate(trade?.createdAt)
      );
    case "offerDate":
    default:
      return parseTradeCalendarDate(k.offerDate);
  }
}

export function tradeDateInRange(trade, sortBy, fromDate, toDate) {
  const d = getTradeSortDate(trade, sortBy);
  if (!d) return false;
  const start = parseTradeCalendarDate(fromDate);
  const end = parseTradeCalendarDate(toDate);
  if (!start || !end) return false;
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return d >= start && d <= end;
}

export function formatReportDate(value) {
  const d = parseTradeCalendarDate(value);
  if (!d) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

export function formatReportMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatTradeAddress(trade) {
  const k = trade?.keyInfo || {};
  return [k.streetNumber, k.streetName, k.unit].filter(Boolean).join(" ").trim();
}

export function getTrustDepositTotal(trade) {
  const records = trade?.trustRecords || [];
  return records.reduce((sum, record) => {
    const amount = parseFloat(
      record.amount || record.depositAmount || record.total || 0,
    );
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
}

function agentRowMatchesSelected(row, selectedAgent) {
  if (!selectedAgent) return true;
  const emp = String(selectedAgent.employeeNo ?? "").trim();
  const id = String(selectedAgent._id ?? "").trim();
  const rowId = String(row.agentId ?? "").trim();
  const rowName = String(row.agentName || "")
    .trim()
    .toLowerCase();
  const selectedName = formatAgentSortName(selectedAgent).toLowerCase();
  const fullName = `${selectedAgent.firstName || ""} ${selectedAgent.lastName || ""}`
    .trim()
    .toLowerCase();

  if (emp && rowId === emp) return true;
  if (id && rowId === id) return true;
  if (rowName && (rowName === selectedName || rowName === fullName)) return true;
  return false;
}

function resolveAgentMaster(row, agentsByEmp, agentsById) {
  const rowId = String(row.agentId ?? "").trim();
  if (rowId && agentsByEmp.has(rowId)) return agentsByEmp.get(rowId);
  if (rowId && agentsById.has(rowId)) return agentsById.get(rowId);
  return null;
}

/**
 * Build commission report rows grouped by agent.
 */
export function buildCommissionReport(trades, agents, filters) {
  const {
    fromDate,
    toDate,
    agentId, // "" = all
    classification, // ALL | LISTING SIDE | ...
    sortBy,
  } = filters;

  const selectedAgent =
    agentId && agents?.length
      ? agents.find((a) => String(a._id) === String(agentId))
      : null;

  const agentsByEmp = new Map();
  const agentsById = new Map();
  for (const a of agents || []) {
    if (a.employeeNo != null) agentsByEmp.set(String(a.employeeNo), a);
    if (a._id != null) agentsById.set(String(a._id), a);
  }

  const filteredTrades = (trades || [])
    .filter((t) => tradeDateInRange(t, sortBy, fromDate, toDate))
    .filter((t) => {
      if (!classification || classification === "ALL") return true;
      return (
        String(t.keyInfo?.classification || "").toUpperCase() ===
        classification.toUpperCase()
      );
    })
    .sort((a, b) => {
      const da = getTradeSortDate(a, sortBy)?.getTime() || 0;
      const db = getTradeSortDate(b, sortBy)?.getTime() || 0;
      if (da !== db) return da - db;
      const na = parseInt(String(a.tradeNumber).replace(/\D/g, ""), 10) || 0;
      const nb = parseInt(String(b.tradeNumber).replace(/\D/g, ""), 10) || 0;
      return na - nb;
    });

  /** @type {Map<string, { groupKey: string, groupLabel: string, employeeNo: string, rows: object[] }>} */
  const groups = new Map();
  const tradeDepositSeen = new Set();
  let totalDepositAmount = 0;

  for (const trade of filteredTrades) {
    const k = trade.keyInfo || {};
    const tradeClass = k.classification || "";
    const list = trade.agentCommissionList || [];

    for (const row of list) {
      if (isReferralRow(row)) continue;
      if (!agentRowMatchesSelected(row, selectedAgent)) continue;

      const master = resolveAgentMaster(row, agentsByEmp, agentsById);
      const employeeNo =
        master?.employeeNo != null
          ? String(master.employeeNo)
          : String(row.agentId || "").trim();
      const groupKey =
        employeeNo ||
        String(row.agentName || master?._id || "unknown").toLowerCase();
      const groupLabel = formatAgentGroupLabel(
        master || {
          employeeNo,
          agentName: row.agentName,
          firstName: "",
          lastName: "",
        },
      );

      const commissionDeal =
        parseFloat(row.amount || row.awardAmount || 0) || 0;
      const deductions =
        parseFloat(row.totalFees || row.feesDeducted || 0) || 0;
      const netDeal =
        parseFloat(row.netCommission || 0) ||
        Math.max(0, commissionDeal - deductions);
      const planInfo = getPlanDisplayName(
        row.feeInfo || master?.feeInfo || "",
      );
      const comPercent =
        row.percentage != null && row.percentage !== ""
          ? Number(row.percentage)
          : null;

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          groupKey,
          groupLabel,
          employeeNo,
          sortName: formatAgentSortName(master) || String(row.agentName || ""),
          rows: [],
        });
      }

      groups.get(groupKey).rows.push({
        tradeNumber: trade.tradeNumber,
        tradeId: trade._id,
        address: formatTradeAddress(trade),
        classification: tradeClass,
        classCode: classificationCode(tradeClass),
        offerDate: formatReportDate(k.offerDate),
        closeDate: formatReportDate(
          k.closeDate || trade.commission?.saleClosingRows?.[0]?.closingDate,
        ),
        volFlag: "",
        comPercent: Number.isFinite(comPercent) ? comPercent : null,
        commissionDeal,
        netDeal,
        planInfo,
        deductions,
        depositAmount: getTrustDepositTotal(trade),
      });
    }

    const tn = String(trade.tradeNumber);
    if (!tradeDepositSeen.has(tn)) {
      // Only count deposit once per trade if at least one agent row was included
      const included = list.some(
        (row) => !isReferralRow(row) && agentRowMatchesSelected(row, selectedAgent),
      );
      if (included) {
        tradeDepositSeen.add(tn);
        totalDepositAmount += getTrustDepositTotal(trade);
      }
    }
  }

  const agentGroups = Array.from(groups.values())
    .map((g) => {
      let commissionBalance = 0;
      let netBalance = 0;
      const rows = g.rows.map((r) => {
        commissionBalance += r.commissionDeal;
        netBalance += r.netDeal;
        return {
          ...r,
          commissionBalance: Math.round(commissionBalance * 100) / 100,
          netBalance: Math.round(netBalance * 100) / 100,
        };
      });
      return {
        ...g,
        rows,
        tradeCount: new Set(rows.map((r) => String(r.tradeNumber))).size,
        endCount: rows.length,
        commissionTotal: Math.round(commissionBalance * 100) / 100,
        netTotal: Math.round(netBalance * 100) / 100,
      };
    })
    .sort((a, b) => {
      const empA = parseInt(a.employeeNo, 10) || 0;
      const empB = parseInt(b.employeeNo, 10) || 0;
      if (empA !== empB) return empA - empB;
      return a.sortName.localeCompare(b.sortName, undefined, {
        sensitivity: "base",
      });
    });

  const tradeCount = agentGroups.reduce((s, g) => s + g.tradeCount, 0);
  // Unique trades across all groups (same trade can appear for multiple agents)
  const uniqueTradeCount = new Set(
    agentGroups.flatMap((g) => g.rows.map((r) => String(r.tradeNumber))),
  ).size;
  const endCount = agentGroups.reduce((s, g) => s + g.endCount, 0);
  const commissionGrand = agentGroups.reduce((s, g) => s + g.commissionTotal, 0);
  const netGrand = agentGroups.reduce((s, g) => s + g.netTotal, 0);
  const deductionsGrand = agentGroups.reduce(
    (s, g) => s + g.rows.reduce((rs, r) => rs + (r.deductions || 0), 0),
    0,
  );

  return {
    agentGroups,
    summary: {
      tradeCount: uniqueTradeCount || tradeCount,
      endCount,
      commissionGrand: Math.round(commissionGrand * 100) / 100,
      netGrand: Math.round(netGrand * 100) / 100,
      deductionsGrand: Math.round(deductionsGrand * 100) / 100,
      totalDepositAmount: Math.round(totalDepositAmount * 100) / 100,
    },
  };
}
